import {
  DictionaryRecordDto,
  OptionalSubtenantDictionaryRecordDto,
} from '@lmt/shared-types';
import {
  DXRG,
  SortingState,
  Table,
  useReactDataGrid,
} from '@lmt/unicorn-shared/app/common/devexpress/ReactGrid/dx-react-grid';
import { useSubtenants } from '@lmt/unicorn-shared/app/features/subtenants/selectors';
import {
  FullHeightGridRoot,
  HideNullTitlesHeaderContent,
  HoverableTable,
  ScrollTableOnScreenToTop,
  StickyHeaderRow,
  useConvertedSorting,
} from '@lmt/unicorn-shared/app/helpers/devextreme-helper';
import {
  NS_DataBlockFields,
  useScopedTranslation,
} from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';

import styles from '@lmt/unicorn-shared/src/app/components/dictionaries/dictionary-record-list/components/DictionaryRecordsTable.module.scss';
import { Paper } from '@mui/material';
import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TableDotMenu } from '../../../common/table/TableDotMenu';
import { useReactDataGridLocalization } from '@lmt/unicorn-shared/app/common/devexpress/ReactGrid/dx-react-grid-localization';

type Props = {
  noResults: boolean;
  data: OptionalSubtenantDictionaryRecordDto[];
  onDelete: (recordId: number) => void;
  onEdit: (recordId: number) => void;
  prefix: string;
  sorting: { sortBy?: string | null; desc?: boolean };
  changeSorting: (sorting: { sortBy?: string | null; desc?: boolean }) => void;
};

function isEditable(record: DictionaryRecordDto, prefix: string): boolean {
  // we can't edit default values
  return !record.value.startsWith(prefix);
}

const TableKeys: { [dictionary: string]: string } = {
  value: 'value',
  option: 'option',
};

export const DictionaryRecordsTable: FC<Props> = React.memo(
  function DictionaryRecordsTable(props) {
    useReactDataGrid();
    const localizationMessages = useReactDataGridLocalization();

    const i18n = useScopedTranslation('dictionaries.list', NS_DataBlockFields);
    const i18nextGlobal = useTranslation();
    const subtenants = useSubtenants();

    const getSubtenantName = useCallback(
      (subtenantId: number): string =>
        subtenants.find((subtenant) => subtenantId === subtenant.id)?.code ??
        '',
      [subtenants],
    );

    useEffect(() => {
      ScrollTableOnScreenToTop();
    }, [props.data]);

    const { sorting, setSorting } = useConvertedSorting(
      props.sorting,
      props.changeSorting,
      ScrollTableOnScreenToTop,
    );

    const tableColumns = useMemo(
      () => [
        {
          name: TableKeys.value,
          title: i18n.t(`columns.${TableKeys.value}` as any)!,
          getCellValue: (row: OptionalSubtenantDictionaryRecordDto) => (
            <>
              {i18nextGlobal.t(row.value as any)}
              {!!row.subtenantId && (
                <span className={styles.patientSubtenantInfo}>
                  {getSubtenantName(row.subtenantId)}
                </span>
              )}
            </>
          ),
        },
        {
          name: TableKeys.option,
          title: '',
          getCellValue: (row: OptionalSubtenantDictionaryRecordDto) => (
            <div
              onClick={(event: any) => event.stopPropagation()}
              style={{ margin: -6 }}
            >
              <TableDotMenu
                options={[
                  {
                    title: i18n.t('options.delete_button'),
                    onClick: () => props.onDelete(row.id),
                    isDeleteItem: true,
                  },
                ]}
              />
            </div>
          ),
        },
      ],
      [],
    );

    const tableColumnExtensions = useMemo<Table.ColumnExtension[]>(
      () => [
        { columnName: TableKeys.value, width: 'auto' },
        { columnName: TableKeys.option, width: '60px' },
      ],
      [],
    );

    const sortingColumnExtensions = useMemo<SortingState.ColumnExtension[]>(
      () => [
        { columnName: TableKeys.value, sortingEnabled: true },
        { columnName: TableKeys.option, sortingEnabled: false },
      ],
      [],
    );

    const PressableRow = useMemo(
      () => (rowProps: Table.DataRowProps) => {
        const editable = isEditable(rowProps.row, props.prefix);
        return (
          <DXRG.Table.Row
            {...rowProps}
            data-enabled={editable}
            onClick={editable ? () => props.onEdit(rowProps.row.id) : undefined}
          />
        );
      },
      [props.onEdit, props.prefix],
    );

    return (
      <Paper style={{ height: '100%', width: '100%' }}>
        <DXRG.Grid
          columns={tableColumns}
          rows={props.data}
          rootComponent={FullHeightGridRoot}
        >
          <DXRG.SortingState
            sorting={sorting}
            onSortingChange={setSorting}
            columnExtensions={sortingColumnExtensions}
            columnSortingEnabled={false}
          />
          <DXRG.IntegratedSorting />
          <DXRG.Table
            messages={localizationMessages}
            columnExtensions={tableColumnExtensions}
            tableComponent={HoverableTable}
            rowComponent={PressableRow}
          />
          <DXRG.TableHeaderRow
            showSortingControls
            contentComponent={HideNullTitlesHeaderContent}
            rowComponent={StickyHeaderRow}
          />
        </DXRG.Grid>
      </Paper>
    );
  },
);
